import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInterviewPreparationAsync,selectInterviewPreparation } from '../candidateSlice/InterviewPreparation/InterviewPreparationSlice';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    useDisclosure,
    Spinner,
    Alert,
    AlertIcon,
    Box,
    SimpleGrid,
    Badge,
    Text,
    Heading,
    Flex,
    Icon,
    VStack,
    HStack,
    Container,
    Divider,
    Skeleton,
    Stack
} from '@chakra-ui/react';
import { FaDownload, FaCalendarAlt, FaFolderOpen, FaBriefcase, FaUserTie } from 'react-icons/fa';
import { INTERVIEW_PREPARATION_VIEW } from '../../common/URLs';

// --- Helper Functions ---
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (error) {
        return 'Invalid Date';
    }
};

// --- Sub-components for better structure ---

const CategoryFilter = ({ categories, selectedCategory, onSelectCategory }) => (
    <Box mb={10}>
        <Heading as="h2" size="md" color="gray.700" mb={4}>
            Filter by Category
        </Heading>
        <Flex wrap="wrap" gap={3}>
            {categories.map((category) => (
                <Button
                    key={category}
                    size="sm"
                    onClick={() => onSelectCategory(category)}
                    colorScheme={selectedCategory === category ? 'blue' : 'gray'}
                    variant={selectedCategory === category ? 'solid' : 'outline'}
                    borderRadius="full"
                >
                    {category}
                </Button>
            ))}
        </Flex>
    </Box>
);

const PreparationCard = ({ resource, onCardClick }) => (
    <Box
        bg="white"
        borderRadius="xl"
        boxShadow="md"
        overflow="hidden"
        transition="all 0.3s ease-in-out"
        _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
        cursor="pointer"
        onClick={() => onCardClick(resource)}
        borderTop="4px solid"
        borderColor="blue.400"
    >
        <VStack p={6} align="start" spacing={3}>
            <Badge colorScheme="blue" variant="subtle" fontSize="xs" py={1} px={2} borderRadius="md">
                {resource.job_department.title}
            </Badge>
            <Heading as="h3" size="md" fontWeight="600" color="gray.800" noOfLines={2}>
                {resource.title}
            </Heading>
            <Text color="gray.600" fontSize="sm" noOfLines={3}>
                {resource.description}
            </Text>
            <HStack color="gray.500" fontSize="xs" spacing={1} pt={2}>

                <Text>Posted: {formatDate(resource.created_at)}</Text>
            </HStack>
        </VStack>
    </Box>
);

const PreparationDetailModal = ({ isOpen, onClose, resource }) => {
    if (!resource) return null;
    const openPdf = (url) => {
        if (!url) {
            console.error("PDF URL is missing.")
            return
        }
        const newTab = window.open(url, "_blank")
        if (!newTab) {
            console.error("Failed to open PDF. Please check your browser settings.")
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered motionPreset="slideInBottom">
            <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(3px)" />
            <ModalContent borderRadius="lg">
                <ModalHeader borderBottomWidth="1px" pb={4}>
                    <VStack align="start" spacing={2}>
                        <Badge colorScheme="blue" variant="solid">{resource.job_department?.title}</Badge>
                        <Heading as="h2" size="lg" color="gray.800">{resource.title}</Heading>
                    </VStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody py={6}>
                    <VStack spacing={4} align="start">
                        <Text fontSize="md" color="gray.700" whiteSpace="pre-wrap">
                            {resource.description}
                        </Text>
                        <Divider />
                        <HStack color="gray.500" fontSize="sm" align="start">
                            <Icon as={FaCalendarAlt} mr={1} />
                            <Text>Published on: {formatDate(resource.created_at)}</Text>
                        </HStack>
                    </VStack>
                </ModalBody>
                <ModalFooter bg="gray.50" borderTopRadius="lg">
                    {resource.attachment && (
                        <Button
                            as="a"
                            onClick={() => openPdf(`${INTERVIEW_PREPARATION_VIEW}${resource.attachment}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            colorScheme="blue"
                            leftIcon={<FaDownload />}
                            mr={3}
                        >
                            View Attachment
                        </Button>
                    )}
                    <Button variant="ghost" onClick={onClose}>
                        Close
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

const LoadingSkeletons = () => (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
        {Array.from({ length: 6 }).map((_, index) => (
            <Stack key={index} bg="white" p={6} borderRadius="xl" boxShadow="md" spacing={4}>
                <Skeleton height="20px" width="100px" borderRadius="md" />
                <Skeleton height="24px" width="80%" borderRadius="md" />
                <Skeleton height="60px" borderRadius="md" />
                <Skeleton height="16px" width="150px" borderRadius="md" />
            </Stack>
        ))}
    </SimpleGrid>
);

const EmptyState = () => (
    <Box textAlign="center" py={16} px={6} bg="white" borderRadius="xl" boxShadow="md">
        <Icon as={FaFolderOpen} color="blue.500" boxSize={16} mb={4} />
        <Heading as="h2" size="lg" fontWeight="600" color="gray.700" mb={2}>
            No Resources Found
        </Heading>
        <Text color="gray.500">
            There are currently no interview preparation resources for the selected category.
        </Text>
    </Box>
);


// --- Main Component ---

const InterviewPreparation = () => {
    const dispatch = useDispatch();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { data, loading, error } = useSelector((state) => state.interviewPreparation);

    const [selectedResource, setSelectedResource] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('All');

    useEffect(() => {
        dispatch(fetchInterviewPreparationAsync());
    }, [dispatch]);

    const handleCardClick = (resource) => {
        setSelectedResource(resource);
        onOpen();
    };

    const resources = useMemo(() => data?.data || [], [data]);

    const categories = useMemo(() => {
        if (!resources.length) return [];
        const uniqueCategories = new Set(resources.map(resource => resource.job_department.title));
        return ['All', ...Array.from(uniqueCategories)];
    }, [resources]);

    const filteredResources = useMemo(() => {
        if (selectedCategory === 'All') return resources;
        return resources.filter(resource => resource.job_department.title === selectedCategory);
    }, [resources, selectedCategory]);

    const renderContent = () => {
        if (loading) {
            return <LoadingSkeletons />;
        }
        if (error) {
            return (
                <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    There was an error fetching resources: {error.message || 'Unknown error'}
                </Alert>
            );
        }
        if (filteredResources.length > 0) {
            return (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                    {filteredResources.map((resource) => (
                        <PreparationCard key={resource.uuid} resource={resource} onCardClick={handleCardClick} />
                    ))}
                </SimpleGrid>
            );
        }
        return <EmptyState />;
    };

    return (
        <div>
            <div style={{ fontFamily: "Inter, sans-serif" }}>
                <div className="py-8 min-h-screen bg-mainBg sm:px-6 sm:py-4">
                    <div className="px-4 sm:px-0.5">

                        {/* Heading */}
                        <div className="mb-10">
                            <div className="flex items-center space-x-3 mb-1">

                                <h1 className="text-3xl font-bold text-gray-800">Interview Preparation</h1>
                            </div>
                            <p className="text-lg text-gray-600 max-w-3xl">
                                Access these curated resources to prepare for your next job interview and increase your chances of success.
                            </p>
                        </div>

                        {/* Category Filter */}
                        {categories.length > 1 && (
                            <CategoryFilter
                                categories={categories}
                                selectedCategory={selectedCategory}
                                onSelectCategory={setSelectedCategory}
                            />
                        )}

                        {/* Render Content */}
                        {renderContent()}

                    </div>
                </div>
            </div>

            {/* Preparation Detail Modal */}
            <PreparationDetailModal
                isOpen={isOpen}
                onClose={onClose}
                resource={selectedResource}
            />
        </div>

    );
};

export default InterviewPreparation;